import type { Permission } from '@/types/composition-functions';
import { PermissionActionEnum, PermissionResourceEnum } from '@/types/composition-functions';

export type Maybe<T> = T | null;

/**
 * Checks if a user has a specific permission.
 *
 * @param user - The user object (usually from 'me' query).
 * @param action - The permission action to check (e.g., 'create', 'view').
 * @param resource - The permission resource to check (e.g., 'students', 'billing').
 * @returns boolean - True if the user has the permission, false otherwise.
 */
export const can = (
  user: unknown,
  actionOrKey: PermissionActionEnum | string,
  resource?: PermissionResourceEnum | string,
): boolean => {
  // `user` is loosely typed at call sites (a `UserType` union, `unknown`, or
  // nullish); read its permissions defensively rather than widening to `any`.
  const permissions = (user as { permissions?: Permission[] } | null | undefined)?.permissions;
  if (!user || !permissions || !Array.isArray(permissions)) {
    return false;
  }

  return permissions.some((p: Permission) => {

    // If resource is not provided, treat actionOrKey as a key (e.g. "students.create")
    if (!resource) {
      return p.key === actionOrKey;
    }

    // Check if both action and resource match
    // We handle both enum comparison and potential string comparison if types are loose
    const actionMatch = p.action === actionOrKey || p.action.toUpperCase() === (actionOrKey as string).toUpperCase();

    // Check both resource name and key
    // resource arg might be the Name ("Result Templates") OR the Key ("result_templates")

    // Normalize by removing underscores and converting to uppercase for loose comparison
    const normalize = (s: string) => s.replace(/_/g, '').toUpperCase();

    const resourceMatch =
            p.resource === resource ||
            p.key === resource ||
            (p.resource && normalize(p.resource) === normalize(resource as string)) ||
            (p.key && normalize(p.key) === normalize(resource as string));

    return actionMatch && resourceMatch;
  });
};

/**
 * Checks if a user has ANY of the provided permissions.
 *
 * @param user - The user object.
 * @param permissions - An array of objects containing action and resource.
 * @returns boolean - True if the user has at least one of the permissions.
 */
export const hasAnyPermission = (
  user: unknown,
  permissions: (string | { action: PermissionActionEnum | string, resource: PermissionResourceEnum | string })[],
): boolean => {
  const userPermissions = (user as { permissions?: Permission[] } | null | undefined)?.permissions;
  if (!user || !userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }

  return permissions.some(requiredPerm => {
    if (typeof requiredPerm === 'string') {
      return can(user, requiredPerm);
    }
    return can(user, requiredPerm.action, requiredPerm.resource);
  });
};

/**
 * Permission Definitions mirroring the backend configuration.
 * Use these constants in the UI to check for permissions.
 */
export const AppPermissions = {
  SchoolProfile: {
    Resource: PermissionResourceEnum.SchoolProfile,
    Actions: {
      View: PermissionActionEnum.View,
      Update: PermissionActionEnum.Update,
    },
  },
  Students: {
    Resource: PermissionResourceEnum.Students,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Teachers: {
    Resource: PermissionResourceEnum.Teachers,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Classes: {
    Resource: PermissionResourceEnum.Classes,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  FinancialRecords: {
    Resource: PermissionResourceEnum.FinancialRecords,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Transportation: {
    Resource: PermissionResourceEnum.Transportation,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Cafeteria: {
    Resource: PermissionResourceEnum.Cafeteria,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Registrations: {
    Resource: PermissionResourceEnum.Registrations,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Attendance: {
    Resource: PermissionResourceEnum.Attendance,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  AttendanceEvents: {
    Resource: PermissionResourceEnum.AttendanceEvents,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  SchoolAssets: {
    Resource: PermissionResourceEnum.SchoolAssets,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Billing: {
    Resource: PermissionResourceEnum.Billing,
    Actions: {
      ViewBillItems: PermissionActionEnum.BillingViewBillItems,
      CreateBillItems: PermissionActionEnum.BillingCreateBillItems,
      UpdateBillItems: PermissionActionEnum.BillingUpdateBillItems,
      DeleteBillItems: PermissionActionEnum.BillingDeleteBillItems,
      ViewBillDetails: PermissionActionEnum.BillingViewBillDetails,
      CreateBills: PermissionActionEnum.BillingCreateBills,
      PublishBills: PermissionActionEnum.BillingPublishBills,
      ViewPaymentsAny: PermissionActionEnum.BillingViewPaymentsAny,
      ViewPaymentsOwn: PermissionActionEnum.BillingViewPaymentsOwn,
      RecordManualPayments: PermissionActionEnum.BillingRecordManualPayments,
      VoidPayments: PermissionActionEnum.BillingVoidPayments,
      ViewPaymentReceiptsAny: PermissionActionEnum.BillingViewPaymentReceiptsAny,
      ViewPaymentReceiptsOwn: PermissionActionEnum.BillingViewPaymentReceiptsOwn,
      ManageStudentWallets: PermissionActionEnum.BillingManageStudentWallets,
      ViewStudentWallets: PermissionActionEnum.BillingViewStudentWallets,
      ManageDiscounts: PermissionActionEnum.BillingManageDiscounts,
      ManageRefunds: PermissionActionEnum.BillingManageRefunds,
      ManageExemptions: PermissionActionEnum.BillingManageExemptions,
      ViewReports: PermissionActionEnum.BillingViewReports,
      ManageFeeSplits: PermissionActionEnum.BillingManageFeeSplits,
      ViewFeeSplits: PermissionActionEnum.BillingViewFeeSplits,
      ViewSplitReports: PermissionActionEnum.BillingViewSplitReports,
      ManageGatewaySettlementAccounts: PermissionActionEnum.BillingManageGatewaySettlementAccounts,
      ViewGatewaySettlementAccounts: PermissionActionEnum.BillingViewGatewaySettlementAccounts,
    },
  },
  CBT: {
    Resource: PermissionResourceEnum.Cbt,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  CbtMonitoring: {
    Resource: PermissionResourceEnum.CbtMonitoring,
    Actions: {
      View: PermissionActionEnum.View,
      Flag: PermissionActionEnum.Flag,
    },
  },
  TimeTable: {
    Resource: PermissionResourceEnum.Timetable,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Permissions: {
    Resource: PermissionResourceEnum.Permissions,
    Actions: {
      View: PermissionActionEnum.View,
      Update: PermissionActionEnum.Update,
    },
  },
  Forms: {
    Resource: PermissionResourceEnum.Forms,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      Review: PermissionActionEnum.Review,
      Convert: PermissionActionEnum.Convert,
    },
  },
  WaiverCodes: {
    Resource: PermissionResourceEnum.WaiverCodes,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Revoke: PermissionActionEnum.Revoke,
    },
  },
  Tickets: {
    Resource: PermissionResourceEnum.Tickets,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      Reply: PermissionActionEnum.Reply,
      ChangeStatus: PermissionActionEnum.ChangeStatus,
      CreateCategory: PermissionActionEnum.CreateCategory,
      Manage: PermissionActionEnum.Manage,
    },
  },
  GradeSystem: {
    Resource: PermissionResourceEnum.GradeSystem,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Results: {
    Resource: PermissionResourceEnum.Results,
    Actions: {
      View: PermissionActionEnum.View,
      UploadScores: PermissionActionEnum.UploadScores,
      DownloadSheets: PermissionActionEnum.DownloadSheets,
      BulkDownloadScores: PermissionActionEnum.BulkDownloadScores,
      BulkDownloadCbt: PermissionActionEnum.BulkDownloadCbt,
      // Broadsheet
      ViewBroadsheet: PermissionActionEnum.ViewBroadsheet,
      CreateBroadsheet: PermissionActionEnum.CreateBroadsheet,
      UpdateBroadsheet: PermissionActionEnum.UpdateBroadsheet,
      DeleteBroadsheet: PermissionActionEnum.DeleteBroadsheet,
    },
  },
  ResultTemplates: {
    Resource: PermissionResourceEnum.ResultTemplates,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  SubjectAssignment: {
    Resource: PermissionResourceEnum.SubjectAssignment,
    Actions: {
      AssignTeacher: PermissionActionEnum.AssignTeacher,
      AssignStudent: PermissionActionEnum.AssignStudent,
      Create: PermissionActionEnum.Create,
      Delete: PermissionActionEnum.Delete,
    },
  },
  CourseRegistrationApproval: {
    Resource: PermissionResourceEnum.CourseRegistrationApproval,
    Actions: {
      View: PermissionActionEnum.View,
      ViewAll: PermissionActionEnum.ViewAll,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      Approve: PermissionActionEnum.Approve,
      Reject: PermissionActionEnum.Reject,
      Endorse: PermissionActionEnum.Endorse,
      Override: PermissionActionEnum.Override,
      Revoke: PermissionActionEnum.Revoke,
      UpdateCourseRegistrationScore: PermissionActionEnum.UpdateCourseRegistrationScore,
      SenateApprove: PermissionActionEnum.SenateApprove,
    },
  },
  StaffRoles: {
    Resource: PermissionResourceEnum.StaffRoles,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Approve: PermissionActionEnum.Approve,
      Reject: PermissionActionEnum.Reject,
      Revoke: PermissionActionEnum.Revoke,
      ViewAll: PermissionActionEnum.ViewAll,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Subjects: {
    Resource: PermissionResourceEnum.Subjects,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  LecturerCourseAllocation: {
    Resource: PermissionResourceEnum.LecturerCourseAllocation,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      AllocateStudents: PermissionActionEnum.AllocateStudents,
    },
  },
  Transcripts: {
    Resource: PermissionResourceEnum.Transcripts,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      Generate: PermissionActionEnum.Generate,
    },
  },
  CmsIntegration: {
    Resource: PermissionResourceEnum.CmsIntegration,
    Actions: {
      View: PermissionActionEnum.View,
      Manage: PermissionActionEnum.Manage,
    },
  },
  Guardians: {
    Resource: PermissionResourceEnum.Guardians,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      Message: PermissionActionEnum.Message,
    },
  },
  SchemeOfWork: {
    Resource: PermissionResourceEnum.SchemeOfWork,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  SchoolAnnouncements: {
    Resource: PermissionResourceEnum.SchoolAnnouncements,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
    },
  },
  LessonNotes: {
    Resource: PermissionResourceEnum.LessonNotes,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      GenerateAi: PermissionActionEnum.GenerateAi,
      Approve: PermissionActionEnum.Approve,
    },
  },
  EducationalResources: {
    Resource: PermissionResourceEnum.EducationalResources,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  WhatsappMessaging: {
    Resource: PermissionResourceEnum.WhatsappMessaging,
    Actions: {
      View: PermissionActionEnum.View,
      Send: PermissionActionEnum.Send,
      Configure: PermissionActionEnum.Configure,
    },
  },
  AiAssistant: {
    Resource: PermissionResourceEnum.AiAssistant,
    Actions: {
      View: PermissionActionEnum.View,
    },
  },
  IdCards: {
    Resource: PermissionResourceEnum.IdCards,
    Actions: {
      Generate: PermissionActionEnum.Generate,
    },
  },
  CustomFields: {
    Resource: PermissionResourceEnum.CustomFields,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Certificates: {
    Resource: PermissionResourceEnum.Certificates,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
      CheckEligibility: PermissionActionEnum.CheckEligibility,
      ApproveGraduation: PermissionActionEnum.ApproveGraduation,
      RevokeGraduation: PermissionActionEnum.RevokeGraduation,
      Generate: PermissionActionEnum.Generate,
      BatchGenerate: PermissionActionEnum.BatchGenerate,
      Review: PermissionActionEnum.Review,
      Approve: PermissionActionEnum.Approve,
      Release: PermissionActionEnum.Release,
      Reject: PermissionActionEnum.Reject,
      Revoke: PermissionActionEnum.Revoke,
      Reissue: PermissionActionEnum.Reissue,
      TemplateManage: PermissionActionEnum.TemplateManage,
      Verify: PermissionActionEnum.Verify,
    },
  },
  Chat: {
    Resource: PermissionResourceEnum.Chat,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Send: PermissionActionEnum.Send,
      Broadcast: PermissionActionEnum.Broadcast,
      MessageParent: PermissionActionEnum.MessageParent,
      Delete: PermissionActionEnum.Delete,
    },
  },
  ChildSchools: {
    Resource: PermissionResourceEnum.ChildSchools,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      TransferStudent: PermissionActionEnum.TransferStudent,
      AssignStaff: PermissionActionEnum.AssignStaff,
      CopyTemplates: PermissionActionEnum.CopyTemplates,
      Export: PermissionActionEnum.Export,
    },
  },
  Campus: {
    Resource: PermissionResourceEnum.Campus,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  CourseExemptions: {
    Resource: PermissionResourceEnum.CourseExemptions,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  CourseEquivalence: {
    Resource: PermissionResourceEnum.CourseEquivalence,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
  Rooms: {
    Resource: PermissionResourceEnum.Rooms,
    Actions: {
      View: PermissionActionEnum.View,
      Create: PermissionActionEnum.Create,
      Update: PermissionActionEnum.Update,
      Delete: PermissionActionEnum.Delete,
    },
  },
};
